import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslations } from '@/i18n';
import type { ReactNode } from 'react';
import type { CityDateValidationResult } from '@/lib/city-date-validation';

interface InvalidCityDatesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entries: CityDateValidationResult[];
}

function IssueBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-[#fff5f5] px-3 py-1 text-sm leading-5 text-[#e5484d]">
      {children}
    </span>
  );
}

export default function InvalidCityDatesDialog({
  isOpen,
  onClose,
  entries,
}: InvalidCityDatesDialogProps) {
  const tValidation = useTranslations('trips.dateValidation');

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const resolveIssueLabel = (entry: CityDateValidationResult): string[] => {
    const labels: string[] = [];

    for (const issue of entry.issues) {
      if (issue.type === 'outside_trip_range') {
        labels.push(tValidation('outsideTripRange'));
      } else if (issue.type === 'overlap' && issue.overlappingCityNames) {
        const citiesList = issue.overlappingCityNames.join(', ');
        labels.push(tValidation('overlapWith', { cities: citiesList }));
      }
    }

    return labels;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-[30rem] gap-0 rounded-3xl border-0 p-0"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{tValidation('title')}</DialogTitle>

        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-[18px] leading-7 font-bold text-black">{tValidation('title')}</h2>
          <Button
            onClick={onClose}
            variant="unstyled"
            size="none"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f1f1f1] transition-colors hover:bg-[#e4e4e4]"
            aria-label={tValidation('close')}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </div>

        <div className="px-6 pb-4">
          <p className="mb-4 text-base leading-6 text-[#292929]">{tValidation('description')}</p>

          <div className="flex flex-col gap-4">
            {entries.map((entry) => (
              <div
                key={entry.cityId}
                className="rounded-2xl border border-[#e5484d] bg-[#fff5f5] px-4 py-3"
              >
                <div className="mb-2 text-[15px] leading-6 font-medium text-[#292929]">
                  {entry.cityName}
                </div>
                <div className="flex flex-wrap gap-2">
                  {resolveIssueLabel(entry).map((label, index) => (
                    <IssueBadge key={`${entry.cityId}-${index}`}>{label}</IssueBadge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 pb-6">
          <Button
            type="button"
            onClick={onClose}
            className="h-auto rounded-full bg-[#242424] px-4 py-3 text-[15px] font-medium text-white transition-colors hover:bg-[#1a1a1a]"
          >
            {tValidation('close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
